import React, { useState } from 'react';
import {
    Box,
    Flex,
    Text,
    Icon,
    Divider,
    Stack,
    Tooltip,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    IconButton,
} from '@chakra-ui/react';
import {
    BadgeCheck,
    BadgeInfo,
    BadgeX,
    Download,
    Calendar,
    HashIcon,
    Car,
    Droplet,
    Settings,
    TowerControl,
    Users,
    Phone,
    Mail,
    MapPin,
} from 'lucide-react';
import ReusableAvatar from 'components/Avatar/ReusableAvatar';
import NoDataFound from 'components/NoDataGraphics/NoDataFound';
import DocumentViewer from 'components/DocumentViewer/DocumentViewer';
import { formatDate } from 'utils/dateFormatter';
import InfoRow from 'components/InfoRow/InfoRow';
import DocumentIcon from 'components/DocumentViewer/DocumentIcon';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ViewVehicle = ({ vehicle, onClose }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentDocument, setCurrentDocument] = useState(null);

    if (!vehicle) return null;

    const {
        registration_number,
        vehicle_make,
        vehicle_model,
        vehicle_body_type,
        vehicle_color,
        chasis_number,
        engine_number,
        year_of_manufacturing,
        created_at,
        documents,
        status,
    } = vehicle.vehicle;

    const {
        first_name,
        last_name,
        email,
        phone_number,
        village,
        district,
        physical_address,
    } = vehicle.user_info || {};

    const fullName = [first_name, last_name].filter(Boolean).join(' ');

    const handleDocumentClick = (doc) => {
        setCurrentDocument({
            url: `${BASE_URL.replace(/\/$/, '')}/${doc.file_path.replace(/^\//, '')}`,
            type: doc.file_extension,
            title: doc.document_type,
            dateUploaded: formatDate(doc.uploaded_at),
        });
        setIsOpen(true);
    };


    const closeModal = () => {
        setIsOpen(false);
        setCurrentDocument(null);
    };

    const downloadDocument = (url, filename) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const vehicleImage = documents?.find(doc => doc.document_type === 'Vehicle Image');
    const vehicleImageUrl = vehicleImage
        ? `${BASE_URL.replace(/\/$/, '')}/${vehicleImage.file_path.replace(/^\//, '')}`
        : null;

    return (
        <Flex direction="column" p={8} width="100%">
            {/* Header */}
            <Flex justifyContent="space-between" alignItems="center">
                <Flex alignItems="center">
                    <ReusableAvatar src={vehicleImageUrl} />
                    <Box>
                        <Text fontSize="2xl" fontWeight="bold" display="flex" alignItems="center">
                            {registration_number}
                            {status === 'Approved' && (
                                <>
                                    <Icon as={BadgeCheck} size={12} color="green.500" stroke="white" ml={1} fill="green.500" />
                                    <Text color="green.500" fontWeight="xs" fontSize="13px" ml={1}> Approved</Text>
                                </>
                            )}
                            {status === 'Pending' && (
                                <>
                                    <Icon as={BadgeInfo} size={12} color="yellow.500" stroke="white" ml={1} fill="yellow.500" />
                                    <Text color="yellow.500" fontWeight="xs" fontSize="13px" ml={1}> Pending</Text>
                                </>
                            )}
                            {status === 'Suspended' && (
                                <>
                                    <Icon as={BadgeX} size={12} color="red.500" stroke="white" ml={1} fill="red.500" />
                                    <Text color="red.500" fontWeight="xs" fontSize="13px" ml={1}> Suspended</Text>
                                </>
                            )}
                            {status === 'Rejected' && (
                                <>
                                    <Icon as={BadgeX} size={12} color="gray.500" stroke="white" ml={1} fill="gray.500" />
                                    <Text color="gray.500" fontWeight="xs" fontSize="13px" ml={1}> Rejected</Text>
                                </>
                            )}
                            {status === 'Deactivated' && (
                                <>
                                    <Icon as={BadgeInfo} size={12} color="purple.500" stroke="white" ml={1} fill="purple.500" />
                                    <Text color="purple.500" fontWeight="xs" fontSize="13px" ml={1}> Deactivated</Text>
                                </>
                            )}
                        </Text>

                        <Text fontSize="sm" color="gray.500" mt={1}>
                            {vehicle_make} • {vehicle_model}
                        </Text>
                    </Box>
                </Flex>
            </Flex>

            <Flex mt={2} alignItems="center">
                <Icon as={Calendar} size={16} color="gray.500" mr={1} />
                <Text fontSize="sm" color="gray.500">
                    Date Added: {formatDate(created_at)}
                </Text>
            </Flex>

            <Divider my={6} />

            {/* Vehicle Info */}
            <Box mb={6}>
                <Text fontSize="md" fontWeight="500" color="gray.500" mb={2}>
                    Vehicle Info
                </Text>
                <Stack spacing={3}>
                    <InfoRow icon={HashIcon} label="Registration Number" value={registration_number} />
                    <InfoRow icon={Car} label="Body Type" value={vehicle_body_type} />
                    <InfoRow icon={Droplet} label="Color" value={vehicle_color} />
                    <InfoRow icon={Settings} label="Chassis Number" value={chasis_number} />
                    <InfoRow icon={TowerControl} label="Engine Number" value={engine_number} />
                    <InfoRow icon={Calendar} label="Year of Manufacturing" value={formatDate(year_of_manufacturing)} />
                </Stack>
            </Box>

            <Divider my={6} />

            {/* Owner Info */}
            <Box mb={6}>
                <Text fontSize="md" fontWeight="500" color="gray.500" mb={2}>
                    Owner Info
                </Text>
                <Stack spacing={3}>
                    <InfoRow icon={Users} label="Name" value={fullName} />
                    <InfoRow icon={Phone} label="Phone" value={phone_number} />
                    <InfoRow icon={Mail} label="Email" value={email} />
                    <InfoRow icon={MapPin} label="Village" value={village} />
                </Stack>
            </Box>

            <Divider my={6} />

            {/* Documents */}
            <Box mb={6}>
                <Text fontSize="md" fontWeight="500" color="gray.500" mb={2}>
                    Vehicle Documents
                </Text>
                {documents && documents.length > 0 ? (
                    <Flex direction="row" wrap="wrap" gap={4} overflowX="auto">
                        {documents
                            .filter((doc) => doc.document_type !== "Payment Proof")
                            .map((doc) => (
                                <Tooltip label="Click to view" hasArrow placement="top" key={doc.document_id}>
                                    <Box
                                        p={2}
                                        border="1px"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        display="flex"
                                        alignItems="center"
                                        _hover={{ cursor: 'pointer', bg: 'gray.100' }}
                                        onClick={() => handleDocumentClick(doc)}
                                        w="calc(50% - 8px)"
                                    >
                                        <DocumentIcon type={doc.file_extension} />
                                        <Box ml={3}>
                                            <Text fontWeight="bold" color="gray.600">{doc.document_type}</Text>
                                            <Text color="gray.500" fontSize="sm">
                                                Uploaded: {formatDate(doc.uploaded_at)}
                                            </Text>
                                        </Box>
                                    </Box>
                                </Tooltip>
                            ))}
                    </Flex>
                ) : (
                    <NoDataFound message="No documents uploaded" width="100px" height="100px" fontSize="sm" fontWeight="bold" />
                )}
            </Box>

            {/* Modal Viewer */}
            <Modal isOpen={isOpen} onClose={closeModal}>
                <ModalOverlay />
                <ModalContent maxWidth="500px" maxHeight="80vh" overflowY="auto">
                    <ModalHeader fontSize="16px">Document Viewer</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {currentDocument ? (
                            <>
                                <DocumentViewer documents={[currentDocument]} />
                                <IconButton
                                    aria-label="Download Document"
                                    icon={<Download />}
                                    onClick={() => downloadDocument(currentDocument.url, currentDocument.title)}
                                    bgGradient="linear-gradient(134.23deg, #071D54 14.41%, #1E3A8A 85.58%)"
                                    color="white"
                                    mt={4}
                                    _hover={{ bgGradient: "linear-gradient(134.23deg, #071D54 14.41%, #1E3A8A 85.58%)" }}
                                    boxShadow="md"
                                    borderRadius="full"
                                    size="lg"
                                />
                            </>
                        ) : (
                            <NoDataFound message="No document to display." />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Flex>
    );
};

export default ViewVehicle;
